// Downscale + re-encode photos before they are embedded in a PDF.
//
// Protocol photos arrive straight off a phone camera: 3000-4000px wide and
// 3-6 MB each. `jsPDF.addImage` embeds whatever bytes it is given, so a
// 30-photo move-out protocol produces a 20-30 MB PDF — which then cannot be
// attached to an email, because receiving mail servers commonly cap at
// 10-25 MB. The photos are drawn into a ~253 x 177pt box, so almost all of
// those pixels were never visible in the first place.
//
// Compressing here, at generation time, is deliberate: the browser has a free
// canvas encoder, while doing the same work server-side in Deno would mean
// pulling in imagescript or ImageMagick-wasm to solve a problem that no longer
// exists once the PDF is small to begin with.
//
// Everything degrades to the original bytes. A photo that cannot be decoded,
// or that a compression pass would make *larger*, is embedded unchanged — a
// smaller PDF is never worth a missing piece of evidence.

/** Longest-edge cap in pixels. */
export const DEFAULT_MAX_EDGE = 1600;

/** JPEG quality for the re-encode pass. */
export const DEFAULT_QUALITY = 0.72;

/**
 * Images already smaller than this are passed through untouched — re-encoding
 * a 180 KB photo costs quality and saves nothing worth having.
 */
export const SKIP_BELOW_BYTES = 300 * 1024;

export interface CompressOptions {
  /** Longest-edge cap in pixels. Defaults to DEFAULT_MAX_EDGE. */
  maxEdge?: number;
  /** JPEG quality, 0-1. Defaults to DEFAULT_QUALITY. */
  quality?: number;
  /** Byte size below which the original is passed through. */
  skipBelowBytes?: number;
}

/**
 * Target dimensions for a downscale that preserves aspect ratio and never
 * upscales. Exported for tests — this is the whole sizing decision.
 */
export function targetDimensions(
  width: number,
  height: number,
  maxEdge: number
): { width: number; height: number } {
  // Guard against a zero/NaN dimension from a half-decoded image: returning
  // the input unchanged lets the caller fall back to the original bytes
  // instead of drawing onto a 0 x 0 canvas.
  if (!(width > 0) || !(height > 0) || !(maxEdge > 0)) {
    return { width, height };
  }
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Is the browser able to decode and re-encode an image off the main document?
 *
 * Vitest runs in `node` by default and jsdom implements neither API, so this
 * is also what keeps compression out of the way in tests.
 */
export function canCompressImages(): boolean {
  return (
    typeof createImageBitmap === "function" &&
    typeof OffscreenCanvas === "function"
  );
}

/**
 * Downscale and re-encode a photo as JPEG. Returns the original blob whenever
 * compression is impossible or counter-productive.
 */
export async function compressImageBlob(
  blob: Blob,
  options: CompressOptions = {}
): Promise<Blob> {
  const maxEdge = options.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = options.quality ?? DEFAULT_QUALITY;
  const skipBelow = options.skipBelowBytes ?? SKIP_BELOW_BYTES;

  if (!canCompressImages()) return blob;
  if (!blob || blob.size === 0) return blob;

  let bitmap: ImageBitmap | null = null;
  try {
    // `from-image` applies the EXIF orientation. This is required rather than
    // optional: re-encoding strips EXIF, so a phone photo tagged "rotate 90"
    // would otherwise have its wrong rotation baked in permanently, with no
    // tag left for any viewer to correct it by.
    bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
  } catch {
    return blob;
  }

  try {
    const { width, height } = targetDimensions(
      bitmap.width,
      bitmap.height,
      maxEdge
    );
    if (!(width > 0) || !(height > 0)) return blob;

    // Already small in both bytes and pixels — nothing to gain.
    const alreadySmall =
      blob.size <= skipBelow &&
      width === bitmap.width &&
      height === bitmap.height;
    if (alreadySmall) return blob;

    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext("2d");
    if (!ctx) return blob;

    // JPEG has no alpha channel, so a transparent PNG would composite onto
    // black. Paint white first — a screenshot pasted into a protocol should
    // look like paper, not like a redaction.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);

    const out = await canvas.convertToBlob({ type: "image/jpeg", quality });

    // A tiny or highly-detailed source can re-encode larger than it started.
    if (!out || out.size === 0 || out.size >= blob.size) return blob;
    return out;
  } catch {
    return blob;
  } finally {
    bitmap.close?.();
  }
}
